import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Glasses, Globe, Key, TrendingUp, ArrowRight } from 'lucide-react';
import { dashboard, user } from '../api';

const StatCard = ({ icon: Icon, label, value, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="bg-[#111118] rounded-2xl p-6 border border-white/5"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-white/60 text-sm mb-1">{label}</p>
        <p className="text-3xl font-bold text-white">{value}</p>
      </div>
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </motion.div>
);

const QuickAction = ({ icon: Icon, title, description, onClick }) => (
  <button
    onClick={onClick}
    className="w-full bg-[#111118] rounded-xl p-4 border border-white/5 hover:border-blue-500/30 transition-all text-left group"
  >
    <div className="flex items-center gap-4">
      <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
        <Icon className="w-5 h-5 text-blue-400" />
      </div>
      <div className="flex-1">
        <h4 className="font-medium text-white">{title}</h4>
        <p className="text-sm text-white/40">{description}</p>
      </div>
      <ArrowRight className="w-5 h-5 text-white/20 group-hover:text-blue-400 transition-colors" />
    </div>
  </button>
);

const OverviewPage = ({ onNavigate }) => {
  const [stats, setStats] = useState({ products: 0, domains: 0 });
  const [apiKey, setApiKey] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [statsData, apiKeyData] = await Promise.all([
          dashboard.getStats(),
          user.getApiKey(),
        ]);
        setStats(statsData);
        setApiKey(apiKeyData.apiKey);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
        <p className="text-white/60">Welcome to VisionLive. Manage your virtual try-on products.</p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard
          icon={Glasses}
          label="Products"
          value={stats.products}
          color="bg-gradient-to-br from-blue-500 to-cyan-500"
          delay={0.1}
        />
        <StatCard
          icon={Globe}
          label="Domain"
          value={stats.domains > 0 ? 'Activated' : 'Deactivated'}
          color="bg-gradient-to-br from-emerald-500 to-teal-500"
          delay={0.2}
        />
        <StatCard
          icon={Key}
          label="API Key"
          value={apiKey ? 'Active' : 'Not Set'}
          color="bg-gradient-to-br from-violet-500 to-purple-500"
          delay={0.3}
        />
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mb-8"
      >
        <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <QuickAction
            icon={Glasses}
            title="Add Product"
            description="Upload a new glasses model or image"
            onClick={() => onNavigate('products')}
          />
          <QuickAction
            icon={Globe}
            title={stats.domains > 0 ? 'Manage Domain' : 'Set Domain'}
            description={stats.domains > 0 ? 'Update or remove your whitelisted domain' : 'Whitelist your domain for the widget'}
            onClick={() => onNavigate('domains')}
          />
          <QuickAction
            icon={Key}
            title="View API Key"
            description="Get your API key for integration"
            onClick={() => onNavigate('api-key')}
          />
          <QuickAction
            icon={TrendingUp}
            title="Implementation Guide"
            description="Learn how to integrate the widget"
            onClick={() => onNavigate('implementation')}
          />
        </div>
      </motion.div>

      {/* Getting Started */}
      {stats.products === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-gradient-to-br from-blue-900/30 to-cyan-900/30 rounded-2xl p-8 border border-blue-500/20"
        >
          <h2 className="text-xl font-bold text-white mb-4">Getting Started</h2>
          <ol className="space-y-4 text-white/70">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                1
              </span>
              <div>
                <p className="font-medium text-white">Add your first product</p>
                <p className="text-sm">Upload 2D images of your glasses</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-blue-500/60 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                2
              </span>
              <div>
                <p className="font-medium text-white">Set your domain</p>
                <p className="text-sm">Whitelist your store's domain (e.g., myshop.com)</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-blue-500/40 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                3
              </span>
              <div>
                <p className="font-medium text-white">Add the widget to your site</p>
                <p className="text-sm">Copy the code snippet and add it to your store</p>
              </div>
            </li>
          </ol>
        </motion.div>
      )}
    </div>
  );
};

export default OverviewPage;

